import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import "../../styles/Dashboard.css";
import { getData, postData } from "../../services/Request";
import { useLoader } from "../../context/LoaderContext";
import { toast } from "react-toastify";
import QRCode from "qrcode";
import { formatDate } from "../../utils/dateFormat";

const parseItemsField = (items) => {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === "string") {
    try {
      const parsed = JSON.parse(items);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Failed to parse items", error);
      return [];
    }
  }
  return [];
};

const calcLineTax = (item) => {
  const qty = parseFloat(item?.quantity) || 0;
  const price = parseFloat(item?.valueSalesExcludingST) || 0;
  const discount = parseFloat(item?.discount) || 0;
  const base = Math.max(qty * price - discount, 0);
  const salesTaxAmount = parseFloat(item?.salesTaxApplicable) || 0;
  const percent =
    (parseFloat(item?.extraTax) || 0) +
    (parseFloat(item?.furtherTax) || 0) +
    (parseFloat(item?.salesTaxWithheldAtSource) || 0);
  return salesTaxAmount + base * (percent / 100);
};

const computeTotals = (items = []) => {
  return items.reduce(
    (acc, item) => {
      const qty = parseFloat(item?.quantity) || 0;
      const price = parseFloat(item?.valueSalesExcludingST) || 0;
      const discount = parseFloat(item?.discount) || 0;
      const gross = qty * price;
      const lineBase = Math.max(gross - discount, 0);
      const tax = calcLineTax(item);
      acc.subtotal += gross;
      acc.tax += tax;
      acc.discount += discount;
      acc.total += lineBase + tax;
      return acc;
    },
    { subtotal: 0, tax: 0, discount: 0, total: 0 }
  );
};

const normalizeSale = (payload) => {
  if (!payload) return null;
  let saleData = null;
  if (payload.sale) {
    saleData = payload.sale;
  } else if (payload?.data?.sale) {
    saleData = payload.data.sale;
  } else if (payload?.data && !Array.isArray(payload.data)) {
    saleData = payload.data;
  } else {
    saleData = payload;
  }
  if (!saleData) return null;
  return {
    ...(saleData || {}),
    items: parseItemsField(saleData?.items),
  };
};

export default function SalesInvoice() {
  const { saleId: saleIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const { showLoader, hideLoader } = useLoader();
  const [sale, setSale] = useState(locationState?.sale || null);
  const [showFbrModal, setShowFbrModal] = useState(false);
  const [fbrSubmitting, setFbrSubmitting] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");

  const items = useMemo(
    () => parseItemsField(sale?.items || locationState?.items),
    [sale?.items, locationState?.items]
  );
  const invoiceNo = sale?.invoiceRefNo || "";
  const saleId = sale?.id || sale?._id || sale?._saleId || saleIdParam || "";
  const displayNumber = invoiceNo || saleId;
  const totals = useMemo(() => computeTotals(items), [items]);
  const fbrInvoiceNumber = useMemo(() => {
    if (!sale) return "";
    return sale?.fbr || "";
  }, [invoiceNo, sale]);

  const hasFbrInvoice = Boolean(fbrInvoiceNumber);

  useEffect(() => {
    const fetchSale = async () => {
      if (!saleIdParam && !saleId) return;
      if (sale) return;
      try {
        showLoader();
        const response = await getData(`api/sales/${saleIdParam || saleId}`);
        setSale(normalizeSale(response));
      } catch (error) {
        toast.error("Invoice details not found.");
        console.error("Failed to load invoice", error);
      } finally {
        hideLoader();
      }
    };
    fetchSale();
  }, [hideLoader, saleId, saleIdParam, showLoader, sale]);

  const handlePrint = () => {
    window.print();
  };

  const handleCreateFbrInvoice = () => {
    setShowFbrModal(true);
  };

  const closeFbrModal = () => setShowFbrModal(false);

  const confirmFbrInvoice = async () => {
    if (!saleId) {
      toast.error("Sale ID missing for FBR invoice.");
      return;
    }
    setFbrSubmitting(true);
    try {
      showLoader();
      await postData(`api/sales/${saleId}/fbr`, {});
      toast.success("FBR invoice created.");
      setShowFbrModal(false);
      // Optionally refresh sale data after creating FBR invoice
      const response = await getData(`api/sales/${saleId}`);
      setSale(normalizeSale(response));
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "FBR invoice not created.";
      toast.error(message);
      console.error("Failed to create FBR invoice", error);
    } finally {
      hideLoader();
      setFbrSubmitting(false);
    }
  };

  useEffect(() => {
    const qrText = fbrInvoiceNumber;
    if (!qrText) {
      setQrCodeDataUrl("");
      return;
    }
    if (typeof qrText === "string" && qrText.startsWith("data:image")) {
      setQrCodeDataUrl(qrText);
      return;
    }
    if (
      typeof qrText === "string" &&
      /^[A-Za-z0-9+/=]+$/.test(qrText) &&
      qrText.length > 100
    ) {
      setQrCodeDataUrl(`data:image/png;base64,${qrText}`);
      return;
    }
    QRCode.toDataURL(String(qrText), { margin: 1, width: 220 })
      .then((url) => setQrCodeDataUrl(url))
      .catch((error) => {
        console.error("Failed to render QR", error);
        setQrCodeDataUrl("");
      });
  }, [fbrInvoiceNumber]);

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="app-content">
        <div className="dashboard-page">
          <Navbar />
          <main className="dashboard">
            <section className="invoice-page">
              <header className="invoice-page__header">
                <div className="invoice-page__brand">
                  <div>
                    <h1>Invoice #{displayNumber || saleId}</h1>
                    <p className="muted-text">
                      Date:{" "}
                      {sale?.invoiceDate ? formatDate(sale.invoiceDate) : "-"}
                    </p>
                    <p className="muted-text">
                      Due: {sale?.dueDate ? formatDate(sale.dueDate) : "-"}
                    </p>
                  </div>
                </div>
                <div className="invoice-page__header-meta">
                  {hasFbrInvoice && (
                    <div className="invoice-page__qr-block">
                      <span className="invoice-page__qr-label">FBR</span>
                      {qrCodeDataUrl ? (
                        <img
                          src={qrCodeDataUrl}
                          alt="FBR QR code"
                          className="invoice-page__qr"
                        />
                      ) : (
                        <div className="invoice-page__qr invoice-page__qr--empty">
                          <small>QR unavailable</small>
                        </div>
                      )}
                      {fbrInvoiceNumber ? (
                        <small className="muted-text"># {displayNumber}</small>
                      ) : null}
                    </div>
                  )}
                  <div className="invoice-page__actions">
                    {!hasFbrInvoice && (
                      <button
                        type="button"
                        className="primary-btn"
                        onClick={handleCreateFbrInvoice}
                      >
                        Create FBR invoice
                      </button>
                    )}
                    <button
                      type="button"
                      className="outline-btn"
                      onClick={handlePrint}
                    >
                      Print Invoice
                    </button>
                    <button
                      type="button"
                      className="outline-btn"
                      onClick={() => navigate(-1)}
                    >
                      Back
                    </button>
                  </div>
                </div>
              </header>

              <div className="invoice-page__parties">
                <div>
                  <p className="eyebrow">Seller</p>
                  <h3>{sale?.sellerBusinessName || "-"}</h3>
                  <p className="muted-text">{sale?.sellerNTNCNIC || "-"}</p>
                  <p className="muted-text">{sale?.sellerProvince || "-"}</p>
                  <p className="muted-text">{sale?.sellerAddress || "-"}</p>
                  <p className="muted-text">
                    Sales person:{" "}
                    {sale?.salesPersonName || sale?.salePerson || "-"}
                  </p>
                </div>
                <div>
                  <p className="eyebrow">Buyer</p>
                  <h3>{sale?.buyerBusinessName || "-"}</h3>
                  <p className="muted-text">{sale?.buyerNTNCNIC || "-"}</p>
                  <p className="muted-text">{sale?.buyerProvince || "-"}</p>
                  <p className="muted-text">{sale?.buyerAddress || "-"}</p>
                </div>
              </div>

              <div className="invoice-page__table-wrapper">
                <table className="invoice-page__table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Discount</th>
                      <th>Tax %</th>
                      <th>Tax</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && (
                      <tr>
                        <td colSpan="8" className="empty-state">
                          No items on this invoice.
                        </td>
                      </tr>
                    )}
                    {items.map((item, index) => {
                      const qty = parseFloat(item?.quantity) || 0;
                      const price =
                        parseFloat(item?.valueSalesExcludingST) || 0;
                      const discount = parseFloat(item?.discount) || 0;
                      const gross = qty * price;
                      const net = Math.max(gross - discount, 0);

                      const tax = calcLineTax(item);
                      const total = net + tax;
                      return (
                        <tr key={`${item?.hsCode || "item"}-${index}`}>
                          <td>{item?.productName || item?.hsCode || "-"}</td>
                          <td>{item?.productDescription || "-"}</td>
                          <td>{qty}</td>
                          <td>{price}</td>
                          <td>{discount}</td>
                          <td>{item?.rate}</td>
                          <td>{tax.toFixed(2)}</td>
                          <td>{total.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="invoice-page__totals">
                <div>
                  <span>Subtotal</span>
                  <strong>{totals.subtotal.toFixed(2)}</strong>
                </div>
                <div>
                  <span>Discount</span>
                  <strong>-{totals.discount.toFixed(2)}</strong>
                </div>
                <div>
                  <span>Tax</span>
                  <strong>{totals.tax.toFixed(2)}</strong>
                </div>
                <div className="invoice-page__grand">
                  <span>Total</span>
                  <strong>{totals.total.toFixed(2)}</strong>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>
      {showFbrModal && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <h3>Create FBR invoice</h3>
            <p>
              Do you want to generate an FBR invoice? This cannot be edited
              later.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="outline-btn"
                onClick={closeFbrModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                onClick={confirmFbrInvoice}
                disabled={fbrSubmitting}
              >
                {fbrSubmitting ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
